import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';

interface Favourite {
  id: string;
  itemType: 'dua' | 'affirmation';
  itemId: string;
}

export const useFavourites = () => {
  const { user, isGuest } = useAuth();
  const { toast } = useToast();
  const [favourites, setFavourites] = useState<Favourite[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setFavourites([]);
    setIsLoading(true);

    const loadFavourites = async () => {
      if (!user && !isGuest) {
        setIsLoading(false);
        return;
      }

      if (isGuest) {
        const saved = localStorage.getItem('ramadanFavourites');
        if (saved) {
          try {
            setFavourites(JSON.parse(saved));
          } catch {
            setFavourites([]);
          }
        }
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('favourites')
          .select('*')
          .eq('user_id', user.id);

        if (error) throw error;

        const mapped = (data || []).map(row => ({
          id: row.id,
          itemType: row.item_type as 'dua' | 'affirmation',
          itemId: row.item_id
        }));

        setFavourites(mapped);
      } catch (error) {
        console.error('Error loading favourites:', error);
      }
      setIsLoading(false);
    };

    loadFavourites();
  }, [user, isGuest]);

  const toggleFavourite = useCallback(async (itemType: 'dua' | 'affirmation', itemId: string) => {
    const existing = favourites.find(f => f.itemType === itemType && f.itemId === itemId);

    if (isGuest || !user) {
      let updated: Favourite[];
      if (existing) {
        updated = favourites.filter(f => !(f.itemType === itemType && f.itemId === itemId));
      } else {
        updated = [...favourites, { id: crypto.randomUUID(), itemType, itemId }];
      }
      setFavourites(updated);
      localStorage.setItem('ramadanFavourites', JSON.stringify(updated));
      return;
    }

    try {
      if (existing) {
        const { error } = await supabase
          .from('favourites')
          .delete()
          .eq('user_id', user.id)
          .eq('item_type', itemType)
          .eq('item_id', itemId);

        if (error) throw error;

        setFavourites(prev => prev.filter(f => !(f.itemType === itemType && f.itemId === itemId)));
        toast({ title: 'Removed from favourites' });
      } else {
        const { data, error } = await supabase
          .from('favourites')
          .insert({
            user_id: user.id,
            item_type: itemType,
            item_id: itemId
          })
          .select()
          .single();

        if (error) throw error;

        setFavourites(prev => [...prev, {
          id: data.id,
          itemType: data.item_type as 'dua' | 'affirmation',
          itemId: data.item_id
        }]);
        toast({ title: 'Added to favourites' });
      }
    } catch (error) {
      console.error('Error toggling favourite:', error);
      toast({
        title: 'Error',
        description: 'Could not update favourites.',
        variant: 'destructive'
      });
    }
  }, [user, isGuest, favourites, toast]);

  const isFavourite = useCallback((itemType: 'dua' | 'affirmation', itemId: string) => {
    return favourites.some(f => f.itemType === itemType && f.itemId === itemId);
  }, [favourites]);

  return {
    favourites,
    isLoading,
    toggleFavourite,
    isFavourite
  };
};
